/**
 * CM インタラクティブ生産国マップ
 *
 * カスタム要素として実装しているため、テーマエディタがセクションを差し替えた際も
 * connectedCallback で自動的に初期化される（shopify:section:load の購読が不要）。
 *
 * 埋め込みJSONのスキーマ（sections/cm-interactive-map.liquid が生成）:
 *   { countries: [ {
 *       name  : 国名
 *       code  : 世界地図SVGの data-country 値（メタオブジェクト未設定時は空）
 *       url   : コレクションURL（任意）
 *       img   : 国地図画像URL（任意）
 *       dx,dy : 世界地図上のラベル位置オフセット(%)（任意）
 *       regions: [ { name, x, y, url, img, apps: [ { name, x, y, url } ] } ]
 *   } ] }
 * 値が空のキーはLiquid側で出力しない。
 */

/** メタオブジェクトに国コードが未設定のときのフォールバック表 */
const FALLBACK_CODE_BY_JP = {
  フランス: 'france',
  イタリア: 'italy',
  スペイン: 'spain',
  ポルトガル: 'portugal',
  ドイツ: 'germany',
  オーストリア: 'austria',
  ギリシャ: 'greece',
  ハンガリー: 'hungary',
  アメリカ: 'usa',
  カナダ: 'canada',
  メキシコ: 'mexico',
  ブラジル: 'brazil',
  チリ: 'chile',
  アルゼンチン: 'argentina',
  ウルグアイ: 'uruguay',
  オーストラリア: 'australia',
  日本: 'honshu',
  イギリス: 'britain',
  中国: 'china',
  インド: 'india',
  トルコ: 'turkey',
  ロシア: 'russia',
};

const ZOOM_MIN = 75;
const ZOOM_MAX = 200;
const ZOOM_STEP = 25;
const MAP_VIEWBOX_W = 950;
const MAP_VIEWBOX_H = 620;

class CmInteractiveMap extends HTMLElement {
  connectedCallback() {
    if (this.dataset.initialized === 'true') return;
    this.dataset.initialized = 'true';
    this.abort = new AbortController();
    this.zoom = 100;
    this.historyStack = [];
    this.markers = [];
    this.dragDistance = 0;
    this.init();
  }

  disconnectedCallback() {
    this.abort?.abort();
    window.removeEventListener('popstate', this.onPopState);
    window.removeEventListener('resize', this.onResize);
    this.dataset.initialized = 'false';
  }

  /* ------------------------------------------------------------------ */
  /* 初期化                                                              */
  /* ------------------------------------------------------------------ */

  get opts() {
    const d = this.dataset;
    return {
      svgUrl: d.svgUrl,
      labelZoom: Number(d.labelZoom || 125),
      jumpZoom: Number(d.jumpZoom || 150),
      wheelZoom: d.wheelZoom || 'ctrl',
      markerAnimation: d.markerAnimation !== 'false',
      designMode: d.designMode === 'true',
    };
  }

  init() {
    this.root = this.querySelector('.cm-interactive-map');
    this.viewport = this.querySelector('.cm-world-map-viewport');
    this.dragLayer = this.querySelector('.cm-world-map-draggable');
    this.mapHost = this.querySelector('.cm-world-map');
    this.overlay = this.querySelector('.cm-world-map__grid-overlay');
    this.wrapper = this.querySelector('.cm-interactive-map__viewport-wrapper');
    this.guide = this.querySelector('.cm-world-map-guide');
    this.quickNav = this.querySelector('.cm-map-quick-nav__list');
    this.quickNavScroll = this.querySelector('.cm-map-quick-nav__scroll-btn');
    this.detailArea = this.querySelector('.cm-detail-map');
    this.detailContainer = this.querySelector('.cm-detail-container');
    this.detailTitle = this.querySelector('.cm-detail-title');
    this.backBtn = this.querySelector('.cm-detail-back');
    this.backBtnText = this.querySelector('.cm-detail-back-text');
    this.wheelHint = this.querySelector('.cm-map-wheel-hint');

    if (!this.viewport || !this.mapHost) return;

    if (!this.opts.markerAnimation) this.root?.classList.add('is-marker-static');

    this.data = this.readData();

    this.bindZoomButtons();
    this.bindDrag();
    this.bindWheel();
    this.bindPinch();
    this.bindKeyboard();
    this.bindQuickNavScroll();
    this.bindDetailNav();

    this.onResize = () => this.updateQuickNavLayout();
    window.addEventListener('resize', this.onResize);

    this.onPopState = (e) => this.handlePopState(e);
    window.addEventListener('popstate', this.onPopState);

    this.loadWorldMap();
  }

  readData() {
    const el = this.querySelector('script[type="application/json"]');
    if (!el) return { countries: [] };
    try {
      const parsed = JSON.parse(el.textContent);
      return { countries: Array.isArray(parsed.countries) ? parsed.countries : [] };
    } catch (err) {
      console.warn('[cm-interactive-map] マップデータのJSONを解析できませんでした', err);
      return { countries: [] };
    }
  }

  /** 世界地図SVGを外部アセットから読み込む（HTMLに直接埋め込まないことでページ重量を削減） */
  async loadWorldMap() {
    const { svgUrl } = this.opts;
    if (!svgUrl) return;

    try {
      const res = await fetch(svgUrl, { signal: this.abort.signal });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const markup = await res.text();
      const host = document.createElement('div');
      host.className = 'cm-world-svg-wrapper';
      host.innerHTML = markup;
      this.mapHost.insertBefore(host, this.overlay);
      this.root?.classList.add('is-map-ready');
      this.buildCountries();
      this.setZoom(this.zoom, { silent: true });
      this.updateQuickNavLayout();
    } catch (err) {
      if (err.name === 'AbortError') return;
      console.warn('[cm-interactive-map] 世界地図SVGを読み込めませんでした', err);
      const skeleton = this.querySelector('.cm-world-map__skeleton');
      if (skeleton) skeleton.textContent = '地図を読み込めませんでした';
    }
  }

  /* ------------------------------------------------------------------ */
  /* 国の紐付けとマーカー生成                                             */
  /* ------------------------------------------------------------------ */

  resolveCode(country) {
    return String(country.code || FALLBACK_CODE_BY_JP[country.name] || '').toLowerCase();
  }

  buildCountries() {
    const byCode = new Map();
    this.data.countries.forEach((c) => {
      const code = this.resolveCode(c);
      if (code) byCode.set(code, c);
    });

    // 地図SVGは同じ国を東西2箇所に複製している。コード単位でまとめて扱う
    const pathsByCode = new Map();
    this.mapHost.querySelectorAll('path.world-country[data-country]').forEach((path) => {
      const code = (path.getAttribute('data-country') || '').toLowerCase();
      if (!byCode.has(code)) return;
      if (!pathsByCode.has(code)) pathsByCode.set(code, []);
      pathsByCode.get(code).push(path);
    });

    const placed = new Set();

    pathsByCode.forEach((paths, code) => {
      const country = byCode.get(code);

      paths.forEach((path) => {
        path.classList.add('is-active-match');
        path.addEventListener('mouseenter', () => this.setCountryHover(code, true));
        path.addEventListener('mouseleave', () => this.setCountryHover(code, false));
        path.addEventListener('click', (e) => {
          if (this.dragDistance > 5) return;
          e.preventDefault();
          this.enterCountry(country);
        });
      });

      // 複製のうち地図の可視範囲に収まるものにマーカーを置く
      for (const path of paths) {
        if (this.createMarker(path, country, code, paths)) {
          placed.add(code);
          break;
        }
      }
    });

    this.renderDiagnostics(byCode, new Set(pathsByCode.keys()), placed);
  }

  /** SVGパスの重心から世界地図上のラベル位置(%)を求める */
  positionOf(path) {
    let shiftX = 0;
    // 地図SVGは東西に複製されたグループを持つ。translate量をtransformから動的に読む
    let node = path.parentElement;
    while (node && node !== this.mapHost) {
      const t = node.getAttribute?.('transform') || '';
      const m = t.match(/translate\(\s*(-?[\d.]+)/);
      if (m) shiftX += parseFloat(m[1]);
      node = node.parentElement;
    }

    const bbox = path.getBBox();
    if (!bbox || bbox.width <= 0) return null;
    return {
      x: ((bbox.x + bbox.width / 2 + shiftX) / MAP_VIEWBOX_W) * 100,
      y: ((bbox.y + bbox.height / 2) / MAP_VIEWBOX_H) * 100,
    };
  }

  /** マーカーを配置できたら true。可視範囲外の複製パスでは false を返す */
  createMarker(path, country, code, siblings) {
    const pos = this.positionOf(path);
    if (!pos) return false;

    const x = pos.x + Number(country.dx || 0);
    const y = pos.y + Number(country.dy || 0);
    if (x < 0 || x > 100) return false;

    const marker = document.createElement('div');
    marker.className = 'cm-world-region-marker';
    marker.style.left = `${x}%`;
    marker.style.top = `${y}%`;
    marker.setAttribute('data-label-country', code);
    marker.innerHTML = `
      <div class="cm-world-region__name">${this.escape(country.name)}</div>
      <div class="cm-world-region__dot"></div>
      <button type="button" class="cm-world-region-marker__hit" aria-label="${this.escape(country.name)}のワインを見る"></button>
    `;
    this.overlay.appendChild(marker);

    // D2: ラベル・ドットからも国を開けるようにする
    const hit = marker.querySelector('.cm-world-region-marker__hit');
    const label = marker.querySelector('.cm-world-region__name');
    const open = (e) => {
      if (this.dragDistance > 5) return;
      e.preventDefault();
      e.stopPropagation();
      this.enterCountry(country);
    };
    hit.addEventListener('click', open);
    label.addEventListener('click', open);
    hit.addEventListener('mouseenter', () => this.setCountryHover(code, true));
    hit.addEventListener('mouseleave', () => this.setCountryHover(code, false));
    hit.addEventListener('focus', () => this.setCountryHover(code, true));
    hit.addEventListener('blur', () => this.setCountryHover(code, false));

    // パス側は装飾のみ担当（クリック判定はマーカーに集約せず両方で受ける）
    siblings.forEach((p) => {
      p.setAttribute('tabindex', '-1');
      p.setAttribute('aria-hidden', 'true');
    });

    this.markers.push({ code, country, x, y, marker });
    this.addQuickNavButton(country, x, y);
    return true;
  }

  setCountryHover(code, on) {
    this.mapHost.querySelectorAll(`path.world-country[data-country="${code}"]`).forEach((p) => {
      p.style.fill = on ? '#4A0E1A' : '';
      p.style.stroke = on ? '#4A0E1A' : '';
    });
    const marker = this.overlay.querySelector(`.cm-world-region-marker[data-label-country="${code}"]`);
    marker?.classList.toggle('is-hovered', on);
  }

  /** B3: 地図に出せなかった国をテーマエディタ上でだけ知らせる */
  renderDiagnostics(byCode, foundInSvg, placed) {
    if (!this.opts.designMode) return;

    const problems = [];
    this.data.countries.forEach((c) => {
      const code = this.resolveCode(c);
      if (!code) {
        problems.push(`「${c.name}」— 地図の国コードが未設定で、フォールバック表にも一致しません`);
      } else if (!foundInSvg.has(code)) {
        problems.push(`「${c.name}」— 国コード <code>${this.escape(code)}</code> に対応するパスが地図SVGにありません`);
      } else if (!placed.has(code)) {
        problems.push(`「${c.name}」— 地図には存在しますが、ラベルの配置位置が範囲外です（位置調整の値をご確認ください）`);
      }
    });

    const host = this.querySelector('.cm-map-diagnostics');
    if (!host) return;
    if (!problems.length) {
      host.hidden = true;
      return;
    }
    host.hidden = false;
    host.innerHTML = `<strong>地図に表示されていない生産国があります</strong>（この案内はテーマエディタでのみ表示されます）<ul>${problems
      .map((p) => `<li>${p}</li>`)
      .join('')}</ul>`;
  }

  /* ------------------------------------------------------------------ */
  /* ズーム                                                              */
  /* ------------------------------------------------------------------ */

  setZoom(value, { silent = false } = {}) {
    const next = Math.max(ZOOM_MIN, Math.min(ZOOM_MAX, Math.round(value)));
    if (next === this.zoom && silent !== true) return;

    const vp = this.viewport;
    const cx = (vp.scrollLeft + vp.clientWidth / 2) / (vp.scrollWidth || 1);
    const cy = (vp.scrollTop + vp.clientHeight / 2) / (vp.scrollHeight || 1);

    this.zoom = next;
    this.dragLayer.style.setProperty('--map-zoom-factor', next);
    this.updateLabelDensity();

    requestAnimationFrame(() => {
      vp.scrollLeft = cx * vp.scrollWidth - vp.clientWidth / 2;
      vp.scrollTop = cy * vp.scrollHeight - vp.clientHeight / 2;
    });
  }

  /** A1: ズームが浅いうちはラベルを隠し、地図そのものを読めるようにする */
  updateLabelDensity() {
    this.overlay?.classList.toggle('is-labels-compact', this.zoom < this.opts.labelZoom);
  }

  bindZoomButtons() {
    this.querySelector('.cm-map-zoom-in')?.addEventListener('click', () => this.setZoom(this.zoom + ZOOM_STEP));
    this.querySelector('.cm-map-zoom-out')?.addEventListener('click', () => this.setZoom(this.zoom - ZOOM_STEP));
  }

  /** D1: ホイールでのズーム */
  bindWheel() {
    const mode = this.opts.wheelZoom;
    if (mode === 'off') return;

    this.viewport.addEventListener(
      'wheel',
      (e) => {
        const wantsZoom = mode === 'direct' || e.ctrlKey || e.metaKey;
        if (!wantsZoom) {
          if (mode === 'ctrl') this.flashWheelHint();
          return; // ページのスクロールを妨げない
        }
        e.preventDefault();
        this.setZoom(this.zoom + (e.deltaY < 0 ? ZOOM_STEP : -ZOOM_STEP));
      },
      { passive: false }
    );
  }

  flashWheelHint() {
    if (!this.wheelHint) return;
    this.wheelHint.classList.add('is-visible');
    clearTimeout(this.wheelHintTimer);
    this.wheelHintTimer = setTimeout(() => this.wheelHint.classList.remove('is-visible'), 1100);
  }

  /** D1: ピンチでのズーム */
  bindPinch() {
    let startDist = 0;
    let startZoom = 100;

    const dist = (t) => Math.hypot(t[0].clientX - t[1].clientX, t[0].clientY - t[1].clientY);

    this.viewport.addEventListener(
      'touchstart',
      (e) => {
        if (e.touches.length !== 2) return;
        startDist = dist(e.touches);
        startZoom = this.zoom;
        this.viewport.classList.add('is-pinching');
      },
      { passive: true }
    );

    this.viewport.addEventListener(
      'touchmove',
      (e) => {
        if (e.touches.length !== 2 || !startDist) return;
        e.preventDefault();
        this.setZoom(startZoom * (dist(e.touches) / startDist));
      },
      { passive: false }
    );

    const end = () => {
      startDist = 0;
      this.viewport.classList.remove('is-pinching');
    };
    this.viewport.addEventListener('touchend', end, { passive: true });
    this.viewport.addEventListener('touchcancel', end, { passive: true });
  }

  /** D5: キーボードでのパンとズーム */
  bindKeyboard() {
    this.viewport.setAttribute('tabindex', '0');
    this.viewport.setAttribute('role', 'application');
    this.viewport.setAttribute('aria-label', '世界地図。矢印キーで移動、プラスとマイナスキーで拡大縮小できます');

    this.viewport.addEventListener('keydown', (e) => {
      const step = e.shiftKey ? 160 : 60;
      const keys = {
        ArrowLeft: () => (this.viewport.scrollLeft -= step),
        ArrowRight: () => (this.viewport.scrollLeft += step),
        ArrowUp: () => (this.viewport.scrollTop -= step),
        ArrowDown: () => (this.viewport.scrollTop += step),
        '+': () => this.setZoom(this.zoom + ZOOM_STEP),
        '=': () => this.setZoom(this.zoom + ZOOM_STEP),
        '-': () => this.setZoom(this.zoom - ZOOM_STEP),
        Home: () => {
          this.setZoom(100);
          this.viewport.scrollTo({ left: 0, top: 0 });
        },
      };
      const fn = keys[e.key];
      if (!fn) return;
      e.preventDefault();
      fn();
      this.guide?.classList.add('is-hidden');
    });
  }

  bindDrag() {
    const vp = this.viewport;
    let isDown = false;
    let startX = 0;
    let startY = 0;
    let scrollLeft = 0;
    let scrollTop = 0;

    vp.addEventListener('mousedown', (e) => {
      isDown = true;
      this.dragDistance = 0;
      vp.classList.add('is-dragging');
      this.guide?.classList.add('is-hidden');
      startX = e.pageX - vp.offsetLeft;
      startY = e.pageY - vp.offsetTop;
      scrollLeft = vp.scrollLeft;
      scrollTop = vp.scrollTop;
    });

    const stop = () => {
      isDown = false;
      vp.classList.remove('is-dragging');
    };
    vp.addEventListener('mouseleave', stop);
    vp.addEventListener('mouseup', stop);

    vp.addEventListener('mousemove', (e) => {
      if (!isDown) return;
      e.preventDefault();
      const walkX = e.pageX - vp.offsetLeft - startX;
      const walkY = e.pageY - vp.offsetTop - startY;
      this.dragDistance = Math.max(Math.abs(walkX), Math.abs(walkY));
      vp.scrollLeft = scrollLeft - walkX * 1.5;
      vp.scrollTop = scrollTop - walkY * 1.5;
    });
  }

  /* ------------------------------------------------------------------ */
  /* クイックナビ                                                        */
  /* ------------------------------------------------------------------ */

  addQuickNavButton(country, x, y) {
    if (!this.quickNav) return;
    const btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 'cm-map-quick-nav__btn';
    btn.textContent = country.name;
    btn.addEventListener('click', () => this.jumpTo(x, y));
    this.quickNav.appendChild(btn);
  }

  /** A3: ジャンプ時にラベルが読めるズームまで寄せてから中央に送る */
  jumpTo(x, y) {
    const run = () => {
      if (this.zoom < this.opts.jumpZoom) this.setZoom(this.opts.jumpZoom);
      requestAnimationFrame(() => {
        const vp = this.viewport;
        vp.scrollTo({
          left: (x / 100) * vp.scrollWidth - vp.clientWidth / 2,
          top: (y / 100) * vp.scrollHeight - vp.clientHeight / 2,
          behavior: 'smooth',
        });
        this.guide?.classList.add('is-hidden');
      });
    };

    if (this.detailArea?.classList.contains('is-visible')) {
      this.showWorld();
      setTimeout(run, 550);
    } else {
      run();
    }
  }

  bindQuickNavScroll() {
    this.quickNavScroll?.addEventListener('click', () => {
      this.quickNav?.scrollBy({ left: 150, behavior: 'smooth' });
    });

    if (!this.quickNav) return;
    let down = false;
    let startX = 0;
    let scrollLeft = 0;
    let moved = 0;

    this.quickNav.addEventListener('mousedown', (e) => {
      if (this.quickNav.scrollWidth <= this.quickNav.clientWidth) return;
      down = true;
      moved = 0;
      this.quickNav.classList.add('is-dragging');
      startX = e.pageX - this.quickNav.offsetLeft;
      scrollLeft = this.quickNav.scrollLeft;
    });
    const stop = () => {
      down = false;
      this.quickNav.classList.remove('is-dragging');
    };
    this.quickNav.addEventListener('mouseleave', stop);
    this.quickNav.addEventListener('mouseup', stop);
    this.quickNav.addEventListener('mousemove', (e) => {
      if (!down) return;
      e.preventDefault();
      this.quickNav.scrollLeft = scrollLeft - (e.pageX - this.quickNav.offsetLeft - startX) * 1.5;
      moved += Math.abs(e.movementX || 1);
    });
    this.quickNav.addEventListener(
      'click',
      (e) => {
        if (moved > 10) {
          e.preventDefault();
          e.stopPropagation();
        }
      },
      true
    );
  }

  updateQuickNavLayout() {
    if (!this.quickNav) return;
    const wrap = this.quickNav.closest('.cm-map-quick-nav');
    this.quickNav.style.paddingRight = '';
    if (this.quickNavScroll) this.quickNavScroll.style.display = 'none';

    if (this.quickNav.scrollWidth > this.quickNav.clientWidth) {
      wrap?.classList.remove('is-centered');
      this.quickNav.style.paddingRight = '44px';
      if (this.quickNavScroll) this.quickNavScroll.style.display = 'flex';
    } else {
      wrap?.classList.add('is-centered');
      this.quickNav.style.paddingRight = '0';
    }
  }

  /* ------------------------------------------------------------------ */
  /* 詳細マップと履歴                                                    */
  /* ------------------------------------------------------------------ */

  bindDetailNav() {
    this.backBtn?.addEventListener('click', () => {
      // D4: 履歴に積んでいるのでブラウザの戻ると挙動を合わせる
      if (this.historyStack.length > 0) history.back();
      else this.showWorld();
    });
  }

  countryIndexOf(country) {
    return this.data.countries.indexOf(country);
  }

  enterCountry(country) {
    if (!country.regions || country.regions.length === 0) {
      if (country.url) window.location.href = country.url;
      return;
    }
    this.pushHistory({ ci: this.countryIndexOf(country) });
    this.zoomTransitionInto(() => this.showDetail(country));
  }

  enterRegion(country, region, regionIndex) {
    if (!region.apps || region.apps.length === 0) {
      if (region.url) window.location.href = region.url;
      return;
    }
    this.pushHistory({ ci: this.countryIndexOf(country), ri: regionIndex });
    this.historyStack.push({ type: 'region', data: region, country });
    this.renderDetail(region, false, country);
  }

  /** クリックした国を中心に寄せてから詳細に切り替える演出 */
  zoomTransitionInto(done) {
    const el = this.dragLayer;
    if (!el || window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      done();
      return;
    }
    el.style.transition = 'transform 0.5s cubic-bezier(0.4, 0, 0.2, 1)';
    el.style.transform = 'scale(2.5)';
    setTimeout(() => {
      done();
      el.style.transition = 'none';
      el.style.transform = 'scale(1)';
    }, 450);
  }

  showDetail(country) {
    this.mapHost.style.display = 'none';
    if (this.wrapper) this.wrapper.style.display = 'none';
    this.detailArea.style.display = 'block';
    setTimeout(() => this.detailArea.classList.add('is-visible'), 10);
    this.historyStack = [{ type: 'country', data: country }];
    this.renderDetail(country, true, null);
  }

  showWorld() {
    this.detailArea.classList.remove('is-visible');
    this.historyStack = [];
    setTimeout(() => {
      this.detailArea.style.display = 'none';
      this.mapHost.style.display = 'block';
      if (this.wrapper) this.wrapper.style.display = 'block';
    }, 500);
  }

  /* --- D4: 階層移動をブラウザ履歴に積む --- */

  pushHistory(state) {
    const payload = { ...(history.state || {}), cmMap: { id: this.dataset.sectionId, ...state } };
    history.pushState(payload, '');
  }

  handlePopState(e) {
    const st = e.state?.cmMap;
    if (!st || st.id !== this.dataset.sectionId) {
      if (this.detailArea?.classList.contains('is-visible')) this.showWorld();
      return;
    }
    const country = this.data.countries[st.ci];
    if (!country) return this.showWorld();

    if (st.ri === undefined) {
      this.showDetail(country);
    } else {
      this.showDetail(country);
      const region = country.regions?.[st.ri];
      if (region) {
        this.historyStack.push({ type: 'region', data: region, country });
        this.renderDetail(region, false, country);
      }
    }
  }

  /* ------------------------------------------------------------------ */
  /* 詳細マップの描画                                                    */
  /* ------------------------------------------------------------------ */

  renderDetail(data, isCountry, parentCountry) {
    this.detailTitle.textContent = data.name;
    if (this.backBtnText) {
      this.backBtnText.textContent = isCountry ? '世界地図に戻る' : '国に戻る';
    }

    const children = (isCountry ? data.regions : data.apps) || [];
    const pins = [];
    const unmapped = [];

    children.forEach((child, index) => {
      const x = Number(child.x);
      const y = Number(child.y);
      const mapped = Number.isFinite(x) && Number.isFinite(y) && x >= 0 && y >= 0;
      (mapped ? pins : unmapped).push({ child, index, x, y });
    });

    const mapHtml = data.img
      ? `<img src="${this.escape(data.img)}" alt="${this.escape(data.name)}の地図" loading="lazy">`
      : `<div class="cm-detail-placeholder">
           <strong>${this.escape(data.name)}の地図画像が未設定です</strong>
           <span>メタオブジェクトに地図画像を登録すると、ここに産地が配置されます。</span>
         </div>`;

    const pinHtml = pins
      .map(({ child, index, x, y }) => {
        const label = `<span class="cm-detail-region__name">${this.escape(child.name)}</span><span class="cm-detail-region__dot"></span>`;
        if (isCountry) {
          return `<button type="button" class="cm-detail-region" style="left:${x}%; top:${y}%;" data-index="${index}">${label}</button>`;
        }
        return `<a href="${this.escape(child.url || '#')}" class="cm-detail-region" style="left:${x}%; top:${y}%;">${label}</a>`;
      })
      .join('');

    const emptyHtml =
      children.length === 0
        ? '<div class="cm-detail-placeholder"><strong>子エリアが登録されていません</strong></div>'
        : '';

    const unmappedHtml = unmapped.length
      ? `<div class="unmapped-wrapper"><h4>その他のエリア</h4><div class="cm-detail-unmapped">${unmapped
          .map(({ child, index }) =>
            isCountry
              ? `<button type="button" class="cm-detail-region-unmapped" data-index="${index}">${this.escape(child.name)}</button>`
              : `<a href="${this.escape(child.url || '#')}" class="cm-detail-region-unmapped">${this.escape(child.name)}</a>`
          )
          .join('')}</div></div>`
      : '';

    this.detailContainer.innerHTML = `<div class="cm-detail-grid"><div class="cm-detail-grid__map">${mapHtml}${pinHtml}${emptyHtml}</div>${unmappedHtml}</div>`;

    if (!isCountry) return;

    const country = data;
    this.detailContainer.querySelectorAll('[data-index]').forEach((el) => {
      el.addEventListener('click', (e) => {
        e.preventDefault();
        const idx = Number(el.getAttribute('data-index'));
        const region = country.regions?.[idx];
        if (region) this.enterRegion(country, region, idx);
      });
    });
  }

  escape(str) {
    return String(str ?? '').replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c]);
  }
}

if (!customElements.get('cm-interactive-map')) {
  customElements.define('cm-interactive-map', CmInteractiveMap);
}
